/*
 * Copyright 2022 Siemens
 */

import {Subject, type SubjectInterface} from '../shared/observer';

export type ApplicationJSON = typeof import('../../public/application.json');

export interface ConfigJSON {
  readonly production: boolean;
  readonly auth: false | {login: string; extend: string; logout: string};
  readonly api: string;
  readonly documentation: null | string;
  readonly module: {
    readonly config: boolean;
    readonly health: boolean;
    readonly ping: boolean;
    readonly routes: boolean;
  };
  readonly limit: {
    readonly param: number;
    readonly request: number;
    readonly file: number;
  };
}

export interface CurrentWorkspace {
  name: string;
  id?: string;
}

export class Settings {
  static readonly #currentWorkspace = new Subject<CurrentWorkspace>({name: 'Personal'});
  static readonly currentWorkspace: SubjectInterface<CurrentWorkspace> = this.#currentWorkspace;

  static #application: ApplicationJSON;
  static currentWorkspaceNamespace = 'default';

  static get application(): ApplicationJSON {
    return this.#application;
  }

  static set application(application: ApplicationJSON) {
    this.#application = application;
  }

  static #configuration: ConfigJSON;

  static get configuration(): ConfigJSON {
    return this.#configuration;
  }

  static set configuration(configuration: ConfigJSON) {
    this.#configuration = configuration;
  }

  static workspaceNamespace(namespace: string | null | undefined): string {
    if (namespace === undefined || namespace === null || namespace.length === 0) {
      this.currentWorkspaceNamespace = 'default';
      return 'default';
    }
    this.currentWorkspaceNamespace = namespace;
    return namespace;
  }

  static retrieveStoredWorkspace(fallback?: {name: string; id?: string} | undefined) {
    const storageName = window.localStorage.getItem(`${this.currentWorkspaceNamespace}/s`);
    const storageId = window.localStorage.getItem(`${this.currentWorkspaceNamespace}/w`);
    if (storageName === null || storageId === null) return fallback === undefined ? {name: 'Personal'} : fallback;
    return {name: storageName, id: storageId};
  }

  static selectWorkspace(workspace: {name: string; id?: string} | undefined): void {
    if (workspace === undefined) this.#currentWorkspace.dispatch({name: 'Personal'});
    else if (workspace.name !== this.#currentWorkspace.value.name || workspace.id !== this.#currentWorkspace.value.id)
      this.#currentWorkspace.dispatch(workspace);

    if (workspace === undefined || workspace.id === undefined) {
      window.localStorage.removeItem(`${this.currentWorkspaceNamespace}/s`);
      window.localStorage.removeItem(`${this.currentWorkspaceNamespace}/w`);
    } else {
      window.localStorage.setItem(`${this.currentWorkspaceNamespace}/s`, workspace.name);
      window.localStorage.setItem(`${this.currentWorkspaceNamespace}/w`, workspace.id);
    }
  }
}
